import { AvatarElementSize } from './AvatarImageCore';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';

const avatarButtonBorder = (
  /**
   * Width of the border shadow.
   */
  borderWidth: number,
  /**
   * Border shadow offset, where 0 will visually be the outer edge of the parent element.
   */
  borderOffset: number,
  /**
   * Color of the border shadow. Should be a custom-property reference.
   */
  color: string
) => {
  /**
   * Describes the diameter of the circle drawn around the element.
   * Is two times the offset of the border plus 100% of the parent dimension.
   */
  const elementDiameter = `calc(100% + ${borderOffset * 2}px)`;
  /**
   * The absolute position top and left offset of the shape.
   * Negative for > 100% diameter, positive for < 100% diameter.
   * Is measured by cutting the difference in diameter in half and inverting its positivity.
   */
  const elementOffset = `calc((${elementDiameter} - 100%) * -0.5)`;
  return css`
    box-shadow: 0 0 0 ${borderWidth}px ${color};
    border-radius: calc(${elementDiameter} * 0.55);
    height: ${elementDiameter};
    left: ${elementOffset};
    top: ${elementOffset};
    width: ${elementDiameter};
  `;
};

export const avatarButton = ({ colors, interactiveStates, zIndex }: Theme) => css`
  --avatar-inner-border-color: transparent;
  --avatar-outer-border-color: transparent;
  appearance: none;
  background: ${colors.bgDarker};
  cursor: pointer;
  position: relative;
  &::after,
  &::before {
    content: '';
    display: block;
    pointer-events: none;
    position: absolute;
    z-index: ${zIndex.bump};
  }
  &::after {
    ${avatarButtonBorder(2, 2, 'var(--avatar-outer-border-color)')};
  }
  &::before {
    ${avatarButtonBorder(2, -2, 'var(--avatar-inner-border-color)')};
  }
  ${interactiveStates.active(css`
    --avatar-image-opacity: 0.5;
  `)};
  ${interactiveStates.disabled(css`
    opacity: 0.25;
  `)};
  ${interactiveStates.focus(css`
    --avatar-outer-border-color: ${colors.borderPrimaryBase};
    outline: none;
  `)}
  ${interactiveStates.hover(css`
    --avatar-image-opacity: 0.7;
  `)};
`;

export const avatarButtonSelected = ({ colors, interactiveStates }: Theme) => css`
  --avatar-outer-border-color: ${colors.borderPrimaryBase};
  ${interactiveStates.focus(css`
    --avatar-inner-border-color: ${colors.borderPrimaryBase};
    --avatar-outer-border-color: ${colors.borderPrimaryBase};
  `)}
`;

const avatarDiameterFromSize = (avatarSize: AvatarElementSize): number => {
  switch (avatarSize) {
    case 'small':
      return 36;
    case 'medium':
      return 44;
    case 'large':
      return 60;
    case 'xlarge':
      return 76;
    default:
      assertNever(avatarSize);
  }
};

export const avatarElementBase = (avatarSize: AvatarElementSize) => {
  const diameter = avatarDiameterFromSize(avatarSize);
  return css`
    border-radius: ${diameter * 0.5}px;
    display: block;
    height: ${diameter}px;
    width: ${diameter}px;
  `;
};

export const avatarImage = css`
  object-fit: cover;
  object-position: center;
  opacity: var(--avatar-image-opacity, 1);
`;
